/**
 * DayButton - Renders the calendar day button with appropriate styling based on type
 * Handles concentric borders for special day types (insemination, predicted-ovulation-insemination)
 */
import { CYCLE_DAY_TEST_START, CYCLE_DAY_TEST_END, CYCLE_DAY_TEST_UNDERLINE_START, CYCLE_DAY_TEST_UNDERLINE_END } from '../../data/constants';

function DayButton({
  type,
  dayNum,
  buttonId,
  dataDate,
  calendarId,
  classes,
  backgroundStyle,
  onClick,
  TYPE_LABELS,
  cd,
  hasTestValue,
  sharedCount,
  eventCount,
  isToday,
}) {
  // Predicted ovulation + insemination day: purple (inner) + pink (middle) + blue (outer) borders
  if (type === 'predicted-ovulation-insemination') {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-[#bfdbfe] p-[2px]">
        <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-[#fce7f3] p-[2px]">
          <button
            id={buttonId}
            data-date={dataDate}
            data-calendar={calendarId}
            className={classes}
            onClick={onClick}
            style={backgroundStyle}
          >
            {isToday && <span className="text-[7px] font-semibold text-[#bfdbfe] leading-none">Today</span>}
            {dayNum}
            {TYPE_LABELS[type] && <span className="text-[7px] font-semibold leading-none">{TYPE_LABELS[type]}</span>}
            {cd && type !== 'peak-test' && type !== 'shared-ov' && type !== 'peak-predicted' && (
              <span className={`text-[7px] leading-none font-black ${type === 'predicted-ovulation' ? (hasTestValue ? 'text-black' : 'text-purple-500') : (type === 'predicted-period' ? 'text-destructive' : (cd === 1 ? 'text-orange-500' : 'text-muted-foreground'))}`}>
                {type === 'predicted-ovulation' ? 'P. OV' : (type === 'predicted-period' ? 'Start' : (cd === 1 ? 'Start' : `CD ${cd}`))}
              </span>
            )}
            {cd >= CYCLE_DAY_TEST_START && cd <= CYCLE_DAY_TEST_END && (
              <span className={`text-[7px] leading-none font-black text-green-500 ${cd >= CYCLE_DAY_TEST_UNDERLINE_START && cd <= CYCLE_DAY_TEST_UNDERLINE_END ? 'underline underline-offset-[2px]' : ''}`}>Test</span>
            )}
            {sharedCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-500 px-0.5 text-[9px] font-bold text-white">
                {sharedCount}
              </span>
            )}
            {eventCount > 0 && (
              <span className="absolute -left-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-0.5 text-[9px] font-bold text-white">
                {eventCount}
              </span>
            )}
            <span className="absolute -right-0.5 -bottom-0.5 text-[10px] leading-none">🍀</span>
          </button>
        </div>
      </div>
    );
  }

  // Insemination day: concentric pink + blue borders
  if (type === 'insemination') {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-[#bfdbfe] p-[2px]">
        <button
          id={buttonId}
          data-date={dataDate}
          data-calendar={calendarId}
          className={`${classes} border-2 border-[#fce7f3]`}
          onClick={onClick}
          style={backgroundStyle}
        >
          {isToday && <span className="text-[7px] font-semibold text-[#bfdbfe] leading-none">Today</span>}
          {dayNum}
          {TYPE_LABELS[type] && <span className="text-[7px] font-semibold leading-none">{TYPE_LABELS[type]}</span>}
          {cd && type !== 'peak-test' && type !== 'shared-ov' && type !== 'peak-predicted' && (
            <span className={`text-[7px] leading-none font-black ${type === 'predicted-ovulation' ? (hasTestValue ? 'text-black' : 'text-purple-500') : (type === 'predicted-period' ? 'text-destructive' : (cd === 1 ? 'text-orange-500' : 'text-muted-foreground'))}`}>
              {type === 'predicted-ovulation' ? 'P. OV' : (type === 'predicted-period' ? 'Start' : (cd === 1 ? 'Start' : `CD ${cd}`))}
            </span>
          )}
          {cd >= CYCLE_DAY_TEST_START && cd <= CYCLE_DAY_TEST_END && (
            <span className={`text-[7px] leading-none font-black text-green-500 ${cd >= CYCLE_DAY_TEST_UNDERLINE_START && cd <= CYCLE_DAY_TEST_UNDERLINE_END ? 'underline underline-offset-[2px]' : ''}`}>Test</span>
          )}
          {sharedCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-500 px-0.5 text-[9px] font-bold text-white">
              {sharedCount}
            </span>
          )}
          {eventCount > 0 && (
            <span className="absolute -left-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-0.5 text-[9px] font-bold text-white">
              {eventCount}
            </span>
          )}
          <span className="absolute -right-0.5 -bottom-0.5 text-[10px] leading-none">🍀</span>
        </button>
      </div>
    );
  }

  // Normal day: simple button
  return (
    <button
      id={buttonId}
      data-date={dataDate}
      data-calendar={calendarId}
      className={classes}
      onClick={onClick}
      style={backgroundStyle}
    >
      {isToday && <span className="text-[7px] font-semibold text-[#bfdbfe] leading-none">Today</span>}
      {dayNum}
      {TYPE_LABELS[type] && <span className="text-[7px] font-semibold leading-none">{TYPE_LABELS[type]}</span>}
      {cd && type !== 'peak-test' && type !== 'shared-ov' && type !== 'peak-predicted' && (
        <span className={`text-[7px] leading-none font-black ${type === 'predicted-ovulation' ? (hasTestValue ? 'text-black' : 'text-purple-500') : (type === 'predicted-period' ? 'text-destructive' : (cd === 1 ? 'text-orange-500' : 'text-muted-foreground'))}`}>
          {type === 'predicted-ovulation' ? 'P. OV' : (type === 'predicted-period' ? 'Start' : (cd === 1 ? 'Start' : `CD ${cd}`))}
        </span>
      )}
      {cd >= CYCLE_DAY_TEST_START && cd <= CYCLE_DAY_TEST_END && (
        <span className={`text-[7px] leading-none font-black text-green-500 ${cd >= CYCLE_DAY_TEST_UNDERLINE_START && cd <= CYCLE_DAY_TEST_UNDERLINE_END ? 'underline underline-offset-[2px]' : ''}`}>Test</span>
      )}
      {sharedCount > 0 && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-500 px-0.5 text-[9px] font-bold text-white">
          {sharedCount}
        </span>
      )}
      {eventCount > 0 && (
        <span className="absolute -left-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-0.5 text-[9px] font-bold text-white">
          {eventCount}
        </span>
      )}
    </button>
  );
}

export default DayButton;
